// components/Sidebar.js
import React, { useEffect, useCallback, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { sidebarItems } from "./sidebarConfig";
import { useAuth } from "../contexts/AuthContext"; // Importar contexto de autenticación
import {
  Home,
  Settings,
  Package,
  Truck,
  User,
  FileText,
  Search,
  List,
  PlusCircle,
  Ship,
  AlertCircle,
  CheckCircle,
  Waypoints,
  Activity,
  ChartBarBig,
  Satellite,
  Ticket,
  DollarSign,
  Fuel,
  Warehouse,
  ChartSpline,
  CodeXml,
  CheckCircleIcon,
  QrCode,
} from "lucide-react";
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { Button } from "./ui/button";

// Función para detectar si es un dispositivo móvil
const isMobileDevice = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  // Patrones para detectar dispositivos móviles
  const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  
  return mobileRegex.test(userAgent);
};

// Componente Sidebar
const Sidebar = ({ isOpen, setSidebarOpen, activeItem, setActiveItem, onLogout }) => {
  const { userRole, permissions, isAuthenticated, sidebarKey, updatePermissionsConfig } = useAuth();
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);
  const updatingPermissionsRef = useRef(false);
  const inactivityTimerRef = useRef(null);
  
  // Estado para controlar si es un dispositivo móvil (combinando detección por tamaño y tipo de dispositivo)
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768 || isMobileDevice());
  
  // Función para cerrar el sidebar después de un periodo de inactividad en dispositivos móviles
  const startInactivityTimer = useCallback(() => {
    // Solo aplicar el timer de inactividad en dispositivos móviles cuando el sidebar está abierto
    if (isMobile && isOpen) {
      // Limpiar cualquier timer existente
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      
      // Configurar nuevo timer para cerrar después de 3 segundos de inactividad
      inactivityTimerRef.current = setTimeout(() => {
        setSidebarOpen(false);
      }, 3000);
    }
  }, [isMobile, isOpen, setSidebarOpen]);
  
  // Cancelar el timer de inactividad cuando hay interacción
  const cancelInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);
  
  // Detectar cambios en el tamaño de la pantalla
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768 || isMobileDevice());
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Iniciar el timer de inactividad cuando cambia el estado del sidebar
  useEffect(() => {
    if (isMobile && isOpen) {
      startInactivityTimer();
    }
    return () => cancelInactivityTimer();
  }, [isMobile, isOpen, startInactivityTimer, cancelInactivityTimer]);

  // Verificar permisos solo cuando cambie de ruta a una nueva
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        // Solo actualizar permisos si cambiamos a una ruta diferente y no hay una actualización en curso
        if (isAuthenticated && userRole && location.pathname !== prevPathRef.current && !updatingPermissionsRef.current) {
          updatingPermissionsRef.current = true;
          prevPathRef.current = location.pathname;
          await updatePermissionsConfig();
          console.log("Permisos actualizados al cambiar a una nueva ruta:", location.pathname);
          updatingPermissionsRef.current = false;
        }
      } catch (error) {
        console.error("Error actualizando permisos:", error);
        updatingPermissionsRef.current = false;
      }
    };
    
    checkPermissions();
  }, [location.pathname, isAuthenticated, userRole, updatePermissionsConfig]);

  // Update activeItem based on current path
  useEffect(() => {
    const currentItem = sidebarItems.find(item => item.path === location.pathname);
    if (currentItem) {
      setActiveItem(currentItem.title);
    }
  }, [location.pathname, setActiveItem]);

  // Filtrar los elementos del sidebar según permisos únicamente - no usar roles de sidebarConfig
  const getFilteredSidebarItems = useCallback(() => {
    // Si el usuario es admin, mostrar todos los elementos
    if (userRole === 'admin') {
      return sidebarItems;
    }
    
    // Para usuarios no admin, filtrar basado en los permisos del contexto
    if (permissions && permissions.length > 0) {
      return sidebarItems.filter(item => {
        // Convertir path a formato comparable con permisos (sin la barra inicial)
        const itemPath = item.path.startsWith('/') ? item.path.substring(1) : item.path;
        // Mostrar el elemento si está en la lista de permisos
        return permissions.includes(itemPath);
      });
    }
    
    // Fallback: si por alguna razón no hay permisos, usar el rol para filtrar
    // Este es un último recurso y debería evitarse llegar aquí
    return sidebarItems.filter(item => {
      return item.roles && item.roles.includes(userRole);
    });
  }, [userRole, permissions]);

  // Función para manejar la navegación
  const handleNavigation = (path, title) => {
    // Guardar el estado que queremos preservar
    localStorage.setItem('lastActiveItem', title);
    
    // Si es móvil, cerrar el sidebar al navegar
    if (isMobile) {
      setSidebarOpen(false);
    }
    
    // Usar window.location.href para forzar una recarga completa y asegurar la navegación
    window.location.href = path;
  };

  // Mapea los iconos de Lucide React
  const iconMap = {
    home: <Home />,
    settings: <Settings />,
    package: <Package />,
    truck: <Truck />,
    user: <User />,
    "file-text": <FileText />,
    search: <Search />,
    list: <List />,
    "plus-circle": <PlusCircle />,
    ship: <Ship />,
    "alert-circle": <AlertCircle />,
    "check-circle": <CheckCircle />,
    "Waypoints": <Waypoints />,
    Activity: <Activity />,
    ChartBarBig: <ChartBarBig />,
    LocalShippingIcon: <LocalShippingIcon />,
    satellite: <Satellite />,
    ticket: <Ticket />,
    DollarSign: <DollarSign />,
    Fuel: <Fuel />,
    Warehouse: <Warehouse />,
    ChartSpline: <ChartSpline />,
    CodeXml: <CodeXml />,
    QrCode: <QrCode />,
  };

  // Si el usuario no está autenticado, no mostrar el sidebar
  if (!isAuthenticated) return null;

  // Los elementos filtrados según permisos
  const filteredSidebarItems = getFilteredSidebarItems();

  const isUtilitiesPage = location.pathname === "/utilities";

  // Registrar para depuración solo cuando cambie algo relevante
  if (process.env.NODE_ENV !== 'production') {
    //console.log('Renderizando sidebar para rol:', userRole);
    //console.log('Con permisos:', permissions);
    //console.log('Elementos visibles:', filteredSidebarItems.map(item => item.title));
  }  return (
    <div 
      key={`sidebar-${sidebarKey}`} // Añadir key para forzar re-renderizado cuando cambian los permisos
      style={{zIndex: isUtilitiesPage ? 2000 : 1000}} // Mayor z-index en página de tableros
      className={`fixed top-0 left-0 h-full bg-gray-800 text-white transition-all duration-300 overflow-y-auto hide-scrollbar ${
        isOpen ? "w-64 sidebar-mobile" : "w-16 md:w-16"
      } ${isMobile ? "sidebar-mobile" : ""}`}
      onMouseEnter={() => {
        // Solo expandir automáticamente en dispositivos no móviles
        if (!isMobile) {
          setSidebarOpen(true);
        }
      }}
      onMouseLeave={() => {
        // Solo contraer automáticamente en dispositivos no móviles
        if (!isMobile) {
          setSidebarOpen(false);
        }
      }}
      onTouchStart={() => {
        // Cancelar el timer de inactividad al interactuar con el sidebar
        cancelInactivityTimer();
      }}
      onTouchEnd={() => {
        // Reiniciar el timer de inactividad después de la interacción
        if (isMobile && isOpen) {
          startInactivityTimer();
        }
      }}
      onClick={() => {
        // Cancelar y reiniciar el timer al hacer clic en el sidebar
        if (isMobile && isOpen) {
          cancelInactivityTimer();
          startInactivityTimer();
        }
      }}
    >
      <div className="flex flex-col justify-between p-2 h-full">
        <div className="space-y-6">
          {filteredSidebarItems.map((item, index) => (
            <div 
              key={`${item.path}-${index}`}
              className={`sidebar-item flex items-center space-x-4 p-2 cursor-pointer transition-colors duration-300 ${
                location.pathname === item.path ? "bg-gray-700 text-white" : "hover:bg-gray-600 text-gray-300"
              }`}
              onClick={() => handleNavigation(item.path, item.title)}
            >
              <div className="text-lg">{iconMap[item.icon]}</div>
              {isOpen && (
                <span className="text-sm">
                  {item.title}
                </span>
              )}
            </div>
          ))}
        </div>        {/* Botones de control para móviles y Cerrar Sesión */}
        <div>
          {/* Botón para cerrar el sidebar en móviles */}
          <Button 
            className="w-full mb-2 md:hidden" 
            variant="destructive" 
            onClick={() => setSidebarOpen(false)}
          >
            Cerrar Menú
          </Button>
          
          {/* Botón de Cerrar Sesión */}
          <Button className="w-full mt-2" variant="outline" onClick={onLogout}>
            Cerrar Sesión
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;